import React from "react";
import { graphql } from "react-relay";
import { useFragment } from "react-relay";
import { useHistory } from "react-router-dom";

import cx from "classnames";
import { noop } from "lodash";
import useResizeObserver from "use-resize-observer";

import { dispatchAnalyticsTrack } from "~/lib/segment";
import { ClipCutModal } from "~/meetingRecorder/ClipCutModal";
import { openClipModal } from "~/meetingRecorder/ClipModal";
import { ClipsPanel } from "~/meetingRecorder/ClipsPanel";
import { MeetingContainerModalNote } from "~/meetingRecorder/MeetingContainerModalNote";
import FloatingRecordingControls from "~/meetingRecorder/RecordingControls/FloatingRecordingControls";
import type { TranscriptMode } from "~/meetingRecorder/TranscriptModal";
import { TranscriptModalContext } from "~/meetingRecorder/TranscriptModalContext";
import { type SpeakerTranscriptSegment, Transcript, type TranscriptRef } from "~/meetingRecorder/TranscriptPanel";
import TranscriptVideo from "~/meetingRecorder/TranscriptVideo";
import { useIsNarrowViewport } from "~/meetingRecorder/utils";
import { useSessionManager } from "~/meetingRecorder/Video/SessionManager";
import MeetingHeader from "~/meetings/MeetingHeader/MeetingHeader";
import { MeetingNote } from "~/meetings/MeetingNote";
import MeetingRecap from "~/meetings/MeetingRecap";
import MeetingTabs, { type MeetingTabType } from "~/meetings/MeetingTabs";
import type { DeletionType } from "~/stream/DeleteNoteModal/DeleteNoteModal";
import { useModals } from "~/ui/modals";
import Selector, { type SelectorOption, type SingleValue } from "~/ui/selectors/Selector";

import AttendeePresenceContextProvider from "./AttendeePresenceContext";
import { useMeetingRecordingWebsocket } from "./hooks/useMeetingRecordingWebsocket";
import { NoteSaveStateProvider } from "./NoteSaveStateContext";
import PreviousMeetings from "./PreviousMeetings";
import RecapRegenerating from "./RecapRegenerating";
import TranscriptTabs, { TRANSCRIPT_TABS, type TranscriptTabType } from "./TranscriptTabs";
import { useShouldShowRecapTab } from "./useShouldShowRecapTab";
import { MEETING_CARD_CONTENT_ELEMENT_ID } from "./utils";

import type { MeetingContent_meetingRecording$data } from "~/__generated__/MeetingContent_meetingRecording.graphql";
import type { MeetingContent_note$key } from "~/__generated__/MeetingContent_note.graphql";
import type { MeetingContent_viewer$key } from "~/__generated__/MeetingContent_viewer.graphql";

import styles from "./MeetingContent.module.css";

/**
 * A hook meant to act as the JS version of the "single-column layout" container query on the meeting-card container.
 * Please use regular container queries if you can help it. This is a workaround for a crash on the desktop app.
 *
 * See this thread for the conversation: https://fellowinsights.slack.com/archives/C08QK1YB9P1/p1750100113860719
 *
 * @returns An object with the following properties:
 * - `isNarrow`: A boolean value matching the query `@container meeting-card (max-width: 740px) { … }`
 * - `ref`: Attach this ref to the element with `container-name: meeting-card`.
 */
export function useShouldShowNarrowContent() {
	const [isNarrow, setIsNarrow] = React.useState(false);
	const { ref } = useResizeObserver({
		onResize: ({ width }) => {
			if (!width) return;

			// Match this check to all the container queries on meeting-card in MeetingContent.module.css for enabling the
			// single-column layout.
			setIsNarrow(width < 740);
		},
	});

	return { isNarrow, ref };
}

graphql`
	fragment MeetingContent_meetingRecording on MeetingRecording @relay(mask: false) {
		id
		title
		meetingCallStart
		meetingCallEnd
		meetingVideoAndTranscriptionDownloadUrl
		meetingRecordedVideoUrl
		subtitlesVttUrl
		thumbnailVttUrl
		isFavourite
		viewerCanDelete
		hasExternalAttendees
		transcript
		transcriptDeletionReason
		isCurrentlyRecording
		activeSummary {
			id
		}
		clips {
			id
			...ClipsPanel_ClipSummary_clip
		}
		...TranscriptPanel_Transcript_meetingRecording @arguments(public: false)
		...MeetingRecap_meetingRecording
		...TranscriptVideo_meetingRecording @arguments(public: false)
		...ClipCutModal_meetingRecording
		...utils_useLongestMeetingRecording_meetingRecordings
		...utils_AddedOrAvailableUntil_meetingRecording
		...utils_useGetURL_recording
		...RecapHistory_meetingRecording
		...Timeline_meetingKeyEvents
		...HighlightsPanel_meetingRecording @arguments(public: false)
		...ChaptersPanel_meetingRecording
		...ClipsPanel_meetingRecording
		...useMeetingRecordingWebsocket_meetingRecording
		...MeetingHeader_meetingRecording

		ownerAccount {
			id
		}
		mediaType
	}
`;

type MeetingContentProps = {
	note: MeetingContent_note$key | null;
	recordings: readonly MeetingContent_meetingRecording$data[];

	closeModal?: () => void;
	initialMeetingRecordingId?: string;
	initialVideoTimestamp?: number;
	viewer: MeetingContent_viewer$key;
	openedTab?: MeetingTabType;
	/**
	 * Must come from the output of `useShouldShowNarrowContent()`.
	 *
	 * GOTCHA: It would seem like you could use a container query on `meeting-card` for this, but as of writing that
	 * causes the app to crash in the desktop app when the breakpoint is hit.
	 *
	 * See this thread for more information: https://fellowinsights.slack.com/archives/C08QK1YB9P1/p1750100113860719
	 */
	isNarrow: boolean;
};

export function MeetingContent({
	note: noteKey,
	recordings,
	closeModal,
	initialMeetingRecordingId,
	initialVideoTimestamp,
	viewer: viewerKey,
	openedTab,
	isNarrow = false,
}: MeetingContentProps) {
	const fromNote = closeModal === undefined;
	const videoRef = React.useRef<HTMLVideoElement>(null);
	// TODO: step 2: add chapters
	// const chaptersRef = React.useRef<ChaptersPanelRef>(null);
	const transcriptRef = React.useRef<TranscriptRef>(null);
	const history = useHistory();

	const viewer = useFragment(
		graphql`
			fragment MeetingContent_viewer on Viewer {
				...HighlightsPanel_viewer
				...MeetingContainerModalNote_viewer
				...MeetingNote_viewer
				...NoteHeader_viewer
				...MeetingPhaseContext_viewer
				...TranscriptPanel_Transcript_viewer
				...StatsPanel_viewer
				...MeetingHeader_viewer
				...PreMeetingControls_viewer

				hasAccessToCopilot

				account {
					__typename
					id
					name
					logo
					allowRecapDownload
					allowRecapEngagementStats
				}

				user {
					id
				}

				videoPlaybackSettings {
					...TranscriptVideo_videoPlaybackSettings
				}
			}
		`,
		viewerKey,
	);

	// Extract user from viewer for websocket authentication
	const { user } = viewer;

	// NOTE: `note` will be null when viewing a recap who's note you don't have access to.
	const note = useFragment(
		graphql`
			fragment MeetingContent_note on INote {
				id
				__typename

				... on Note {
					viewerCanEdit
					eventGuid
				}

				...utils_useMeetingRecorderStatus_note
				...TranscriptModalContent_note
				...TranscriptPanel_note
				...MeetingContainerModalNote_note
				...MeetingNote_note
				...useShouldShowRecapTab_note
				...MeetingHeader_note
				...FloatingRecordingControls_note

				...HighlightsPanel_note
				...StatsPanel_note
			}
		`,
		noteKey,
	);

	const longestMeetingRecording = React.useMemo(
		() =>
			recordings?.reduce((longest, current) => {
				if (!longest) return current;
				if (!current.meetingCallEnd || !current.meetingCallStart) return longest;
				if (!longest.meetingCallEnd || !longest.meetingCallStart) return current;

				const currentDuration =
					new Date(current.meetingCallEnd).getTime() - new Date(current.meetingCallStart).getTime();
				const longestDuration =
					new Date(longest.meetingCallEnd).getTime() - new Date(longest.meetingCallStart).getTime();

				return currentDuration > longestDuration ? current : longest;
			}, recordings[0]),
		[recordings],
	);

	const [selectedRecordingId, setSelectedRecordingId] = React.useState<string | undefined>(
		initialMeetingRecordingId ?? longestMeetingRecording?.id,
	);

	// State for Notes/AI Notes tabs on larger widths, where the video is on the right
	const [currentNotesTab, _setCurrentNotesTab] = React.useState<MeetingTabType>(
		Boolean(longestMeetingRecording) ? "recap" : "agenda",
	);

	// State for transcript/clips tabs on larger widths, where thevideo is on the right
	const [currentTranscriptOrClipsTab, _setCurrentTranscriptOrClipsTab] =
		React.useState<TranscriptTabType>("Transcript");

	// State for the combined Notes/AI Notes/Transcript/Clips tabs on narrow widths, where the video is on the top
	const [currentCombinedGroupTab, _setCurrentCombinedGroupTab] = React.useState<MeetingTabType | TranscriptTabType>(
		currentNotesTab,
	);

	// A combined "setCurrentTab" function. Manages setting the state for the state variables above
	const setCurrentTab = React.useCallback((nextCurrentTab: MeetingTabType | TranscriptTabType) => {
		if ((TRANSCRIPT_TABS as readonly string[]).includes(nextCurrentTab)) {
			_setCurrentTranscriptOrClipsTab(nextCurrentTab as TranscriptTabType);
		} else {
			_setCurrentNotesTab(nextCurrentTab as MeetingTabType);
		}
		_setCurrentCombinedGroupTab(nextCurrentTab);
	}, []);

	const onDeleteExecuted = React.useCallback(
		(type: DeletionType) => {
			if (type === "EVERYTHING") {
				if (fromNote) {
					history.push("/meetings");
				} else {
					closeModal?.();
				}
			} else if (type === "RECAP") {
				const firstOtherRecording = recordings?.find(recording => recording.id !== selectedRecordingId);
				if (firstOtherRecording) {
					setSelectedRecordingId(firstOtherRecording.id);
				} else {
					setSelectedRecordingId(undefined);
					setCurrentTab("agenda");
				}
			}
		},
		[fromNote, history, closeModal, recordings, selectedRecordingId, setCurrentTab],
	);

	const onPartChange = React.useCallback((val: SingleValue<SelectorOption>) => {
		if (!val) return;

		setSelectedRecordingId(val.value);
	}, []);

	React.useEffect(() => {
		if (openedTab) {
			setCurrentTab(openedTab);
		}
	}, [openedTab, setCurrentTab]);

	const showRecapTab = useShouldShowRecapTab(note);

	const recapParts = React.useMemo(() => {
		return recordings
			?.filter(
				(
					recording,
				): recording is typeof recording & {
					meetingCallStart: string;
					meetingCallEnd: string;
				} => !!recording.meetingCallStart && !!recording.meetingCallEnd,
			)
			.map((recording, i) => {
				return {
					label: `Part ${i + 1}`,
					value: recording.id,
				};
			});
	}, [recordings]);

	React.useEffect(() => {
		transcriptRef.current?.closeCommentsPanel();
	}, [selectedRecordingId, transcriptRef]);

	const selectedRecording = recordings?.find(recording => recording.id === selectedRecordingId);

	// Use websocket hook for real-time updates
	const websocketState = useMeetingRecordingWebsocket({
		meetingRecordingKey: selectedRecording ?? null,
		userId: user.id,
	});

	const [thumbnailTrack, setThumbnailTrack] = React.useState<TextTrack | null>(null);
	const [mode, setMode] = React.useState<TranscriptMode>("default");

	const { analyticsFingerprint, setNoteId, setMeetingRecordingSummaryId } = React.useContext(TranscriptModalContext);

	React.useEffect(() => {
		setNoteId(note?.id ?? null);
		setMeetingRecordingSummaryId(selectedRecording?.activeSummary?.id);
	}, [note, selectedRecording?.activeSummary?.id, setMeetingRecordingSummaryId, setNoteId]);

	const narrowViewport = useIsNarrowViewport();
	useSessionManager(videoRef.current, selectedRecording?.id ?? null, null);

	const [duration, setDuration] = React.useState<number | null>(null);

	const onTimestampClick = React.useCallback((timestamp: number) => {
		const video = videoRef.current;
		if (!video) {
			return;
		}

		video.currentTime = Math.max(0, timestamp + 0.1);
		if (video.paused) {
			video.play();
		}
	}, []);

	const onTranscriptSegmentClick = React.useCallback(
		(transcriptSegment: SpeakerTranscriptSegment) => {
			const video = videoRef.current;
			if (!video) return;

			// Subtract half a second to make sure you get the first word; the browser
			// might round this number.
			video.currentTime = Math.max(0, transcriptSegment.words[0].start_timestamp - 0.5);
			video.play();

			dispatchAnalyticsTrack("Meeting recordings transcript segment click", {
				fingerprint: analyticsFingerprint,
				note_id: note?.id, // eslint-disable-line camelcase
			});
		},
		[note, videoRef, analyticsFingerprint],
	);

	const pauseVideo = React.useCallback(() => {
		videoRef.current?.pause();
	}, [videoRef]);

	React.useEffect(() => {
		if (selectedRecording) {
			dispatchAnalyticsTrack("[AI Copilot] Recap Viewed", {
				fingerprint: analyticsFingerprint,
				source: "Modal",
				/* eslint-disable camelcase */
				note_id: note?.id,
				meeting_recording_summary_id: selectedRecording.activeSummary?.id,
				has_external_attendees: selectedRecording?.hasExternalAttendees,
				/* eslint-enable camelcase */
			});
		}
	}, [analyticsFingerprint, note?.id, selectedRecording]);

	const modals = useModals();
	const openClipCutModal = React.useCallback(
		async (initialTimestamp?: number, initialEndTimestamp?: number) => {
			if (!selectedRecording) return;

			const cutModalResult = await modals.open<boolean>(({ close }) => (
				<ClipCutModal
					meetingRecordingKey={selectedRecording}
					note={note}
					videoPlaybackSettings={viewer.videoPlaybackSettings}
					allowRecapDownload={viewer.account.allowRecapDownload}
					close={close}
					initialTimestamp={initialTimestamp}
					initialEndTimestamp={initialEndTimestamp}
					fromNote={fromNote}
				/>
			));

			if (cutModalResult) {
				setCurrentTab("Clips");
			}
		},
		[
			selectedRecording,
			modals,
			note,
			viewer.videoPlaybackSettings,
			viewer.account.allowRecapDownload,
			fromNote,
			setCurrentTab,
		],
	);

	const openClipTrimmer = React.useCallback(async () => {
		if (videoRef.current && !videoRef.current.paused) {
			// If the recording video is currently playing, pause it, so user can play the clip video
			videoRef.current.pause();
		}

		if (!selectedRecording) return;

		const videoTime = videoRef.current?.currentTime;
		openClipCutModal(videoTime);
	}, [selectedRecording, videoRef, openClipCutModal]);

	const onClipSelect = React.useCallback(
		(clipId: string) => {
			if (videoRef.current && !videoRef.current.paused) {
				// If the recording video is currently playing, pause it, so user can play the clip video
				videoRef.current.pause();
			}

			openClipModal(clipId);
		},
		[videoRef],
	);

	const onVideoTimeUpdate = React.useCallback(() => {
		if (!videoRef.current) return;

		const { currentTime } = videoRef.current;

		transcriptRef.current?.handleTimeUpdate(currentTime);
		// chaptersRef.current?.handleTimeUpdate(currentTime);
	}, []);

	const onVideoLoadedMetadata = React.useCallback(() => {
		if (!videoRef.current) return;
		if (isNaN(videoRef.current.duration)) return;

		const textTracks = videoRef.current.textTracks;
		for (let i = 0; i < textTracks.length; i += 1) {
			const track = textTracks[i];
			if (track.kind === "metadata" && track.label === "thumbnails") {
				setThumbnailTrack(track);
				break;
			}
		}

		setDuration(videoRef.current.duration);
	}, [setDuration, videoRef]);

	const [hasTrackedLowLanguageConfidenceScoreBannerShown, setHasTrackedLowLanguageConfidenceScoreBannerShown] =
		React.useState(false);

	const trackLowLanguageConfidenceScoreBannerShown = React.useCallback(
		analyticsData => {
			if (hasTrackedLowLanguageConfidenceScoreBannerShown) return;
			dispatchAnalyticsTrack("[AI Copilot] Low language confidence score banner displayed", analyticsData);
			setHasTrackedLowLanguageConfidenceScoreBannerShown(true);
		},
		[hasTrackedLowLanguageConfidenceScoreBannerShown],
	);

	const showClipsTab = Boolean(selectedRecording?.clips && selectedRecording.clips.length > 0);
	React.useEffect(() => {
		if (!showClipsTab && currentCombinedGroupTab === "Clips") {
			setCurrentTab("Transcript");
		}
	}, [showClipsTab, currentCombinedGroupTab, setCurrentTab]);

	const noteContent = !note ? null : fromNote ? (
		<MeetingNote note={note} viewer={viewer} onTimestampClick={onTimestampClick} />
	) : (
		<MeetingContainerModalNote
			key="agenda"
			note={note}
			viewer={viewer}
			accountId={viewer.account.id}
			closeModal={closeModal ?? noop}
			onTimestampClick={onTimestampClick}
		/>
	);

	const transcriptContent = selectedRecording ? (
		<Transcript
			key="transcript-el"
			ref={transcriptRef}
			note={note}
			fromNote={fromNote}
			narrowViewport={narrowViewport}
			meetingRecording={selectedRecording}
			viewer={viewer}
			transcriptDeletionReason={selectedRecording.transcriptDeletionReason}
			transcript={(selectedRecording.transcript || []) as SpeakerTranscriptSegment[]}
			onSegmentClick={onTranscriptSegmentClick}
			pauseVideo={pauseVideo}
			videoDuration={duration}
			closeModal={closeModal ?? noop}
			makeSpaceForAskCopilotButton={false}
			mode={mode}
			setMode={setMode}
			trackLowLanguageConfidenceScoreBannerShown={trackLowLanguageConfidenceScoreBannerShown}
		/>
	) : null;

	const clipsContent = selectedRecording ? (
		<ClipsPanel key="clips-el" meetingRecording={selectedRecording} onClipSelect={onClipSelect} />
	) : null;

	return (
		<NoteSaveStateProvider>
			<AttendeePresenceContextProvider>
				<div className={fromNote ? styles.meetingContentContainer : styles.modalContentContainer}>
					<MeetingHeader
						note={note}
						viewer={viewer}
						onClose={closeModal}
						selectedRecording={selectedRecording ?? null}
						onDeleteExecuted={onDeleteExecuted}
					/>

					{selectedRecording && showRecapTab ? (
						<div className={styles.meetingContent}>
							{/*
							 * .meetingContent reverses these in CSS when there's enough space
							 * Necessary to enable a scrolling on narrow widths, while keeping the video on top.
							 */}
							{!websocketState.regenerating && (
								<div className={styles.videoArea}>
									<div className={styles.videoWrapper}>
										<TranscriptVideo
											key={selectedRecording.id}
											ref={videoRef}
											meetingRecording={selectedRecording}
											initialVideoTimestamp={initialVideoTimestamp}
											videoPlaybackSettings={viewer.videoPlaybackSettings}
											isMiniPlayerActive={false}
											toggleMiniPlayer={noop}
											thumbnailTrack={thumbnailTrack}
											openClipTrimmer={openClipTrimmer}
											inClippingMode={false}
											// Omitted since the parts selector is now outside the video
											parts={[]}
											setSelectedRecordingId={noop}
											allowRecapDownload={viewer?.account.allowRecapDownload}
											// The following are videoProps
											onTimeUpdate={onVideoTimeUpdate}
											onLoadedMetadata={onVideoLoadedMetadata}
											isCollapsed={false}
											setIsCollapsed={noop}
										>
											<track label="Subtitles" kind="subtitles" src={selectedRecording.subtitlesVttUrl} />
											{selectedRecording.thumbnailVttUrl && (
												<track default label="thumbnails" kind="metadata" src={selectedRecording.thumbnailVttUrl} />
											)}
										</TranscriptVideo>
									</div>

									<div className={styles.dedicatedTranscriptAndClipsArea}>
										{showClipsTab && (
											<TranscriptTabs currentTab={currentTranscriptOrClipsTab} setCurrentTab={setCurrentTab} />
										)}

										{currentTranscriptOrClipsTab === "Transcript" ? transcriptContent : clipsContent}
									</div>
								</div>
							)}

							<div className={cx(styles.notesArea, { [styles.regenerating]: websocketState.regenerating })}>
								{(note || recapParts.length > 1) && (
									<div className={styles.notesAreaHeaderRow}>
										{note && (
											<MeetingTabs
												currentNotesTab={currentNotesTab}
												currentCombinedGroupTab={currentCombinedGroupTab}
												setCurrentTab={setCurrentTab}
												noteKey={note}
												showClipsTab={showClipsTab}
												isNarrow={isNarrow}
											/>
										)}
										{recapParts.length > 1 && (
											<Selector
												condensed
												options={recapParts}
												value={recapParts.find(tab => tab.value === selectedRecordingId)}
												onChange={onPartChange}
												className={styles.partsSelector}
											/>
										)}
									</div>
								)}

								{isNarrow && currentCombinedGroupTab === "Clips" ? (
									clipsContent
								) : isNarrow && currentCombinedGroupTab === "Transcript" ? (
									transcriptContent
								) : (isNarrow && currentCombinedGroupTab === "recap") || (!isNarrow && currentNotesTab === "recap") ? (
									websocketState.regenerating ? (
										<RecapRegenerating />
									) : (
										<div id={MEETING_CARD_CONTENT_ELEMENT_ID} className={styles.recapWrapper}>
											<MeetingRecap
												key="ai-notes"
												meetingRecording={selectedRecording}
												note={note}
												viewer={viewer}
												duration={duration}
												onTimestampClick={onTimestampClick}
												openClipCutModal={openClipCutModal}
												onRegenerateClicked={websocketState.onRegenerateClicked}
											/>
										</div>
									)
								) : (
									<div id={MEETING_CARD_CONTENT_ELEMENT_ID} className={styles.meetingInternalContent}>
										{noteContent}
									</div>
								)}
							</div>
						</div>
					) : (
						<div id={MEETING_CARD_CONTENT_ELEMENT_ID} className={styles.simpleNoteWrapper}>
							{noteContent}
						</div>
					)}
					{note && fromNote && <PreviousMeetings noteId={note.id} />}
					{note && viewer.hasAccessToCopilot && (
						<FloatingRecordingControls viewer={viewer} note={note} eventGuid={note.eventGuid ?? null} />
					)}
				</div>
			</AttendeePresenceContextProvider>
		</NoteSaveStateProvider>
	);
}
